import React, { FC, useState } from 'react';
import { Box, Typography, Fade } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { IBuildPhoto } from '../../../../models';
import { Modal } from '../../../../components';

interface IPhotoViewerModalProps {
  selectedPhoto: IBuildPhoto | null;
  onClose: () => void;
}

export const PhotoViewerModal: FC<IPhotoViewerModalProps> = ({ selectedPhoto, onClose }) => {
  const classes = useStyles();
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleCloseModal = () => {
    setImageLoading(false);
    setCurrentSlide(0);
    onClose();
  };

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
  };

  // Carousel settings for multiple images
  const carouselSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    arrows: true,
    beforeChange: (oldIndex: number, newIndex: number) => {
      handleSlideChange(newIndex);
    }
  };

  return (
    <Modal open={!!selectedPhoto} onClose={handleCloseModal} maxWidth="md" transitionDuration={300}>
      <Fade in={!!selectedPhoto}>
        <Box className={classes.modalContent}>
          {selectedPhoto && (
            <>
              {selectedPhoto.photoFiles.length === 1 ? (
                <Box display="flex" justifyContent="center" className={classes.imageContainer}>
                  <img
                    src={selectedPhoto.photoFiles[0].photoUrl}
                    alt="preview"
                    className={classes.modalImage}
                    onLoad={handleImageLoad}
                    style={{ opacity: imageLoading ? 0 : 1 }}
                  />
                </Box>
              ) : (
                <Box className={classes.carouselContainer}>
                  <Slider {...carouselSettings}>
                    {selectedPhoto.photoFiles.map((photoFile, index) => (
                      <Box
                        key={photoFile.buildCommunicationPhotoFileId}
                        className={classes.slideContainer}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          className={classes.imageContainer}
                        >
                          <img
                            src={photoFile.photoUrl}
                            alt={`preview ${index + 1}`}
                            className={classes.modalImage}
                            onLoad={handleImageLoad}
                            style={{ opacity: imageLoading ? 0 : 1 }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Slider>
                  <Box className={classes.imageCounter}>
                    <Typography variant="caption" color="textSecondary">
                      {selectedPhoto.photoFiles.length > 1
                        ? `${currentSlide + 1} of ${selectedPhoto.photoFiles.length}`
                        : ''}
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedPhoto.comment && (
                <Box mt={1} p={1} className={classes.commentContainer}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <em>Comment:</em>
                  </Typography>
                  <Typography variant="body1">{selectedPhoto.comment}</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

const useStyles = makeStyles(theme => ({
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
    maxHeight: '80vh',
    overflowY: 'auto',
    minHeight: '400px', // Set minimum height to prevent layout shift
    display: 'flex',
    flexDirection: 'column'
  },
  carouselContainer: {
    position: 'relative',
    '& .slick-slider': {
      position: 'relative'
    },
    '& .slick-list': {
      overflow: 'hidden'
    },
    '& .slick-track': {
      display: 'flex',
      alignItems: 'center'
    },
    // Ensure default slick arrows are visible and properly styled
    '& .slick-prev, & .slick-next': {
      zIndex: 1,
      width: '30px',
      height: '30px',
      '&:before': {
        fontSize: '25px',
        color: '#666'
      }
    },
    '& .slick-prev': {
      left: '1px'
    },
    '& .slick-next': {
      right: '1px'
    }
  },
  slideContainer: {
    position: 'relative'
  },
  imageContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    flexShrink: 0
  },
  modalImage: {
    maxWidth: 'calc(100% - 80px)', // Leave space for arrows (40px each side + 20px margin)
    maxHeight: '60vh', // Reduced height to ensure arrows are always visible
    objectFit: 'contain',
    transition: 'opacity 0.3s ease-in-out' // Help with smoother image loading
  },
  imageCounter: {
    position: 'absolute',
    bottom: '0px',
    right: '0px',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem'
  },
  commentContainer: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[50]
  }
}));
